import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Paper } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import {
  useCampaigns,
  useCampaignReports,
  useGmailOAuth,
  useCampaignDrawer
} from './hooks'

import { getUrlParams } from '../mailTemplate/src/services/urlServices'
import { getBooleanUserRolesByUser } from '../mailTemplate/src/utils/helpers'

import CampaignList from './components/CampaignList'
import CreateCampaignDrawer from '../mailTemplate/src/components/atomic/organisms/CreateCampaignDrawer'

const getB2BAccountId = (userGSD, userRoles) => {
  return userRoles.B2B_PERSONNEL
    ? getUrlParams()?.b2bAccountId || userGSD?.ownedB2bAccountId
    : userGSD?.ownedB2bAccountId
}

const initializeUserData = (userGSD, userRoles, b2bAccountId) => {
  const currentUserData = userRoles.B2B_PERSONNEL
    ? userGSD?.followedB2bAccountIds?.[b2bAccountId]
    : userGSD

  return {
    ...currentUserData,
    id: b2bAccountId
  }
}

const getUserRoles = (userGSD) => {
  return getBooleanUserRolesByUser(userGSD)
}

const NoAccountAccess = () => {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 600,
          color: 'primary.main',
          mb: 3
        }}
      >
        {t('mailTemplate.campaigns.campaignsTitle')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          px: 2
        }}
      >
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            p: 8,
            maxWidth: 600,
            width: '100%',
            backgroundColor: '#fef7f7',
            border: '2px solid #ffcdd2',
            borderRadius: 3
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                backgroundColor: '#f44336',
                borderRadius: '50%',
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ErrorOutlineIcon sx={{ color: 'white', fontSize: 48 }}/>
            </Box>
          </Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#d32f2f'
            }}
          >
            {t('mailTemplate.campaigns.accessErrorTitle')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#424242',
              lineHeight: 1.6
            }}
          >
            {t('mailTemplate.campaigns.b2bAccountIdError')}
          </Typography>
        </Paper>
      </Box>
    </Box>
  )
}

const Campaigns = ({ userGSD }) => {
  const { t } = useTranslation()

  const userRoles = useMemo(() => getUserRoles(userGSD), [userGSD])
  const b2bAccountId = useMemo(() => getB2BAccountId(userGSD, userRoles), [userGSD, userRoles])

  const [userData, setUserData] = useState(null)

  const { campaignReportsFetching, refreshCampaignReport } = useCampaignReports(t)
  const { campaigns, campaignReports, setCampaignReports, fetchCampaigns, isLoading } = useCampaigns(b2bAccountId)

  useGmailOAuth(t)

  const {
    isOpen: isDrawerOpen,
    openDrawer,
    closeDrawer
  } = useCampaignDrawer(userData, t)

  const handleRefreshCampaignReport = useCallback((b2bCampaignId) => {
    refreshCampaignReport(b2bCampaignId, setCampaignReports)
  }, [refreshCampaignReport, setCampaignReports])

  const handleFetchCampaigns = useCallback(async () => {
    const campaignsData = await fetchCampaigns()

    if (!campaignsData?.length) {
      openDrawer()
    }
  }, [fetchCampaigns, openDrawer])

  useEffect(() => {
    const userData = initializeUserData(userGSD, userRoles, b2bAccountId)
    setUserData(userData)
  }, [userGSD, userRoles, b2bAccountId])

  useEffect(() => {
    handleFetchCampaigns()
  }, [handleFetchCampaigns])

  if (!b2bAccountId) {
    return <NoAccountAccess/>
  }

  return (
    <>
      <CampaignList
        campaigns={campaigns}
        campaignReports={campaignReports}
        fetchCampaigns={handleFetchCampaigns}
        onCreateCampaign={openDrawer}
        refreshCampaignReport={handleRefreshCampaignReport}
        campaignReportsFetching={campaignReportsFetching}
        isLoading={isLoading}
      />
      <CreateCampaignDrawer
        userGSD={userGSD}
        b2bAccountId={b2bAccountId}
        open={isDrawerOpen}
        setOpen={closeDrawer}
        fetchCampaigns={handleFetchCampaigns}
      />
    </>
  )
}

export default Campaigns
